import type { ShadowComparisonResult } from "@expert/gaia";
import type { Logger } from "@expert/logging";
import type React from "react";

import { GaiaWebsocketProvider } from "@expert/gaia";
import { useMemo } from "react";

import { env } from "@/config";
import { useActiveTask } from "@/sdk";
import { useFeatureFlag } from "@/shared-utils";

import { addSimplrRealtimeTools } from "./addSimplrRealtimeTools";
import { selectWebsocketUrl } from "./selectWebsocketUrl";
import { useReportRealtimeWebsocket } from "./useReportRealtimeWebsocket";

interface PartnerAwareWebsocketProviderProps {
    identity: string;
    token: string;
    logger: Logger;
    /** Simplr shadow websocket base URL (comparison-only dark launch). */
    shadowUrl?: string;
    /** Whether the caller wants the Simplr shadow connection opened for comparison. */
    shadowEnabled?: boolean;
    /** Invoked for each GAIA/Simplr shadow message difference. */
    onShadowComparison?: (result: ShadowComparisonResult) => void;
    children?: React.ReactNode;
}

/**
 * Wraps {@link GaiaWebsocketProvider} and points the live realtime connection at either the
 * Simplr websocket (the GAIA replacement during the GAIA -> LogicBuilder migration) or the
 * default GAIA websocket, based on the `isSimplrWebsocketLiveEnabled` feature flag.
 *
 * Must be rendered inside the `AgentSdkProvider` so {@link useActiveTask} can resolve the active
 * task: the flag is fetched with its partner in context, so its targeting decides which calls
 * migrate. Partner is read directly from the active task (not the more general `usePartner`,
 * which falls back to the expert's static assigned partner when no task is active) so that an
 * idle expert, or the moment before a new task registers, always resolves to `undefined` and
 * therefore GAIA, rather than a stale partner. The same partner is also checked client-side in
 * {@link selectWebsocketUrl} as a backstop, since the flag is fetched asynchronously and can
 * briefly lag behind a just-switched call's partner. In production that backstop restricts the
 * cutover to the Simplr-eligible partner allowlist; outside production any partner is eligible, so
 * the cutover can be exercised locally without needing an eligible call. Switching to a call with a different
 * customer re-resolves the flag and re-evaluates the URL, and `react-use-websocket`
 * transparently reconnects to the new endpoint.
 *
 * The Simplr shadow dark launch (comparison-only) is passed through from the caller and is
 * suppressed automatically when this connection is already live on Simplr, so the shadow never
 * compares Simplr against itself.
 *
 * Realtime tools that only exist on Simplr (see {@link addSimplrRealtimeTools}) are appended to
 * messages bound for the Simplr socket: on the live connection when it is Simplr, and on every
 * mirrored shadow message, since the shadow is always Simplr. GAIA never receives them.
 *
 * Which provider is live (GAIA vs Simplr) is reported via {@link useReportRealtimeWebsocket} — to a
 * store the timeline reads for a visible "Simplr" badge, plus analytics and FullStory — so the
 * cutover is observable from the frontend, which otherwise looks identical for either provider.
 */
export function PartnerAwareWebsocketProvider({
    identity,
    token,
    logger,
    shadowUrl,
    shadowEnabled = false,
    onShadowComparison,
    children,
}: PartnerAwareWebsocketProviderProps) {
    const partner = useActiveTask()?.partner;
    const { data: isSimplrWebsocketLiveEnabled = false } = useFeatureFlag<boolean>("isSimplrWebsocketLiveEnabled", {
        partner,
    });

    const url = useMemo(
        () =>
            selectWebsocketUrl({
                isSimplrWebsocketLiveEnabled,
                gaiaWebsocketUrl: env.gaiaWebsocketUrl,
                simplrWebsocketUrl: env.simplrWebsocketUrl,
                partner,
                isProdMode: env.isProdMode,
            }),
        [isSimplrWebsocketLiveEnabled, partner],
    );

    const isLive = url === env.simplrWebsocketUrl;
    // Never shadow-compare Simplr against itself: once the live connection is already Simplr, drop the shadow.
    const isShadowActive = shadowEnabled && !isLive;

    // Publishes GAIA-vs-Simplr provider to the store (timeline badge), analytics (EDP), and FullStory,
    // and keeps the existing log line, so the live cutover is observable from the frontend.
    useReportRealtimeWebsocket({ isLive, partner, isSimplrWebsocketLiveEnabled, logger });

    return (
        <GaiaWebsocketProvider
            identity={identity}
            application="expert-workspace"
            token={token}
            url={url}
            logger={logger}
            shadowUrl={shadowUrl}
            shadowEnabled={isShadowActive}
            onShadowComparison={onShadowComparison}
            transformOutbound={isLive ? addSimplrRealtimeTools : undefined}
            shadowTransformOutbound={addSimplrRealtimeTools}
        >
            {children}
        </GaiaWebsocketProvider>
    );
}
